import React, { useState } from 'react';
import styled from 'styled-components';
import { apiService } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';

interface LoginScreenProps {
  onLogin: () => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
  const { login } = useAuth();
  const [mode, setMode] = useState<'login' | 'reset'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const ok = await login(email, password);
    setLoading(false);
    if (ok) {
      onLogin();
    } else {
      setError('Invalid email or password');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await apiService.authResetPassword(email);
      setResetSent(true);
    } catch {
      setError('Failed to send reset email');
    }
    setLoading(false);
  };

  return (
    <Container>
      <Card>
        <LogoArea>
          {!logoError ? (
            <Logo
              src="/bspl_logo.svg"
              alt="BSPL"
              onError={() => setLogoError(true)}
            />
          ) : (
            <LogoFallback>BSPL</LogoFallback>
          )}
          <AppName>Cooling Circuit Designer</AppName>
        </LogoArea>

        {mode === 'login' && (
          <>
            <Title>Sign In</Title>
            <Form onSubmit={handleLogin}>
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              {error && <ErrorMsg>{error}</ErrorMsg>}
              <SubmitBtn type="submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </SubmitBtn>
            </Form>
            <Links>
              <LinkBtn onClick={() => { setMode('reset'); setError(''); }}>
                Forgot password?
              </LinkBtn>
            </Links>
          </>
        )}

        {mode === 'reset' && (
          <>
            <Title>Reset Password</Title>
            {resetSent ? (
              <InfoText>If the email exists, a reset link has been sent. Check your inbox.</InfoText>
            ) : (
              <>
                <InfoText>Enter your email to receive a password reset link</InfoText>
                <Form onSubmit={handleResetPassword}>
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                  {error && <ErrorMsg>{error}</ErrorMsg>}
                  <SubmitBtn type="submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </SubmitBtn>
                </Form>
              </>
            )}
            <Links>
              <LinkBtn onClick={() => { setMode('login'); setError(''); setResetSent(false); }}>
                Back to sign in
              </LinkBtn>
            </Links>
          </>
        )}
      </Card>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #202D61 0%, #1a2450 50%, #151d3d 100%);
  font-family: 'Sora', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  width: 400px;
  max-width: 90vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const LogoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
  margin-bottom: 12px;
`;

const LogoFallback = styled.span`
  font-family: 'Syne', sans-serif;
  font-weight: 700;
  font-size: 28px;
  color: #202D61;
  margin-bottom: 12px;
`;

const AppName = styled.span`
  font-size: 14px;
  color: #5f6368;
  letter-spacing: 0.3px;
`;

const Title = styled.h2`
  margin: 0 0 16px 0;
  font-size: 20px;
  color: #202D61;
  text-align: center;
`;

const InfoText = styled.p`
  margin: 0 0 16px 0;
  font-size: 13px;
  color: #666;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  font-family: inherit;
  &:focus { border-color: #1a73e8; box-shadow: 0 0 0 2px rgba(26,115,232,0.15); }
`;

const SubmitBtn = styled.button`
  padding: 10px;
  background: #202D61;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  margin-top: 4px;
  &:hover { background: #2a3a7a; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMsg = styled.div`
  color: #d93025;
  font-size: 13px;
  padding: 4px 0;
`;

const Links = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  color: #1a73e8;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  &:hover { text-decoration: underline; }
`;
